import { Link } from "react-router-dom";

const features = [
  {
    icon: "ri-building-2-line",
    title: "Direct from Canadian Factories",
    desc: "Skip the middleman. Order straight from verified Canadian manufacturers at wholesale prices.",
  },
  {
    icon: "ri-leaf-line",
    title: "Canadian Farm Fresh",
    desc: "Maple syrup, organic grains, dairy, honey — sourced directly from certified Canadian farms.",
  },
  {
    icon: "ri-truck-line",
    title: "Ships Within Canada",
    desc: "All orders fulfilled from Canadian warehouses. No border delays, no import duties.",
  },
  {
    icon: "ri-translate-2",
    title: "English & French Support",
    desc: "Full bilingual service for all provinces including Quebec. Support en français disponible.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Verified & Certified",
    desc: "Every supplier is background-checked. Products meet Health Canada and CFIA standards.",
  },
  {
    icon: "ri-money-dollar-circle-line",
    title: "CAD & USD Pricing",
    desc: "Pay in Canadian or US dollars. Transparent pricing with no hidden fees.",
  },
];

const canadaSuppliers = [
  {
    name: "True North Maple Co.",
    location: "Quebec, Canada",
    category: "Farm — Maple Syrup & Honey",
    products: 24,
    rating: 4.9,
    image: "https://public.readdy.ai/ai/img_res/dfed49885fb898fb21fa3a06cdac63f0.jpg",
    badge: "Top Rated",
  },
  {
    name: "Northern Timber Works",
    location: "British Columbia, Canada",
    category: "Factory — Wood & Garden Products",
    products: 38,
    rating: 4.8,
    image: "https://readdy.ai/api/search-image?query=canadian%20timber%20factory%20workshop%20with%20craftsmen%20working%20on%20wooden%20furniture%20and%20garden%20products%2C%20british%20columbia%20forest%20background%20visible%20through%20windows%2C%20professional%20industrial%20facility%2C%20warm%20lighting&width=300&height=200&seq=csup2&orientation=landscape",
    badge: "Verified",
  },
  {
    name: "Prairie Natural Products",
    location: "Alberta, Canada",
    category: "Farm — Organic Grains & Oils",
    products: 31,
    rating: 4.8,
    image: "https://readdy.ai/api/search-image?query=vast%20golden%20wheat%20and%20canola%20fields%20in%20alberta%20canada%20prairie%20at%20sunset%2C%20beautiful%20agricultural%20landscape%2C%20farm%20buildings%20in%20distance%2C%20dramatic%20sky%2C%20professional%20landscape%20photography&width=300&height=200&seq=csup3&orientation=landscape",
    badge: "Organic Certified",
  },
  {
    name: "Wisconsin Dairy Farms Co.",
    location: "Ontario, Canada",
    category: "Farm — Dairy & Cheese",
    products: 19,
    rating: 4.9,
    image: "https://readdy.ai/api/search-image?query=canadian%20dairy%20farm%20with%20black%20and%20white%20cows%20grazing%20on%20green%20ontario%20pasture%2C%20red%20barn%20in%20background%2C%20blue%20sky%20with%20clouds%2C%20clean%20and%20modern%20farm%20facility%2C%20professional%20agricultural%20photography&width=300&height=200&seq=csup4&orientation=landscape",
    badge: "Farm Fresh",
  },
];

const stats = [
  { value: "4", label: "Canadian Provinces" },
  { value: "112+", label: "Canada-Made Products" },
  { value: "3-6", label: "Days Delivery" },
  { value: "98.8%", label: "On-Time Rate" },
];

const CanadaDirectSection = () => (
  <section id="canada-direct" className="py-16 bg-white">
    <div className="max-w-[1400px] mx-auto px-6">

      {/* Header */}
      <div className="flex flex-col lg:flex-row gap-12 items-center mb-14">
        {/* Left text */}
        <div className="lg:w-1/2">
          <div className="inline-flex items-center gap-2 bg-[#FF0000]/10 border border-[#FF0000]/20 text-[#CC0000] text-xs font-semibold px-3 py-1.5 rounded-full mb-4">
            <span>🇨🇦</span> Direct from Canada
          </div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 leading-tight mb-4">
            Verified Factory &amp; Farm<br />
            <span className="text-[#CC0000]">Suppliers Made in Canada</span>
          </h2>
          <p className="text-gray-600 text-sm leading-relaxed mb-6">
            Canadian buyers — shop directly from verified Canadian factories and farms. No import duties, no border delays. Everything ships from within Canada, straight to your door in 3–6 days. Bilingual support in English and French for all provinces.
          </p>

          {/* Stats row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
            {stats.map((s) => (
              <div key={s.label} className="bg-[#fdf8f4] border border-[#f0e8e0] rounded-lg p-3 text-center">
                <div className="text-xl font-extrabold text-[#CC0000]">{s.value}</div>
                <div className="text-gray-500 text-[10px] mt-0.5 leading-tight">{s.label}</div>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-3">
            <Link
              to="/products"
              className="flex items-center gap-2 bg-[#CC0000] text-white px-6 py-3 rounded-full font-semibold text-sm hover:bg-[#aa0000] transition-colors cursor-pointer whitespace-nowrap"
            >
              <span>🇨🇦</span> Shop Canada Products
            </Link>
            <Link
              to="/supplier-register"
              className="flex items-center gap-2 border-2 border-[#CC0000] text-[#CC0000] px-6 py-3 rounded-full font-semibold text-sm hover:bg-[#CC0000] hover:text-white transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-building-2-line"></i> Register as Supplier
            </Link>
          </div>
        </div>

        {/* Right image */}
        <div className="lg:w-1/2">
          <div className="relative rounded-xl overflow-hidden h-80 md:h-[400px]">
            <img
              src="https://readdy.ai/api/search-image?query=beautiful%20canadian%20landscape%20collage%20showing%20maple%20syrup%20farm%2C%20modern%20factory%20building%2C%20wheat%20fields%2C%20and%20dairy%20farm%20all%20in%20one%20scenic%20view%2C%20autumn%20colors%2C%20canadian%20flag%20elements%2C%20professional%20commercial%20photography%2C%20warm%20golden%20light&width=700&height=400&seq=canada-direct-hero&orientation=landscape"
              alt="Canadian Factory and Farm Suppliers"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
            {/* Floating card */}
            <div className="absolute bottom-4 left-4 right-4">
              <div className="bg-white/95 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-xs font-bold text-gray-800">Live — Canadian Warehouse Status</span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div>
                    <div className="text-sm font-bold text-[#CC0000]">9,820</div>
                    <div className="text-[10px] text-gray-500">Items in Stock</div>
                  </div>
                  <div>
                    <div className="text-sm font-bold text-[#CC0000]">218</div>
                    <div className="text-[10px] text-gray-500">Orders Today</div>
                  </div>
                  <div>
                    <div className="text-sm font-bold text-[#CC0000]">98.8%</div>
                    <div className="text-[10px] text-gray-500">On-Time Rate</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Feature grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-14">
        {features.map((f) => (
          <div
            key={f.title}
            className="flex flex-col items-center text-center p-4 rounded-lg bg-[#fdf8f4] border border-[#f0e8e0] hover:border-[#CC0000]/30 transition-colors"
          >
            <div className="w-10 h-10 flex items-center justify-center bg-[#CC0000]/10 rounded-full mb-3">
              <i className={`${f.icon} text-[#CC0000] text-lg`}></i>
            </div>
            <div className="font-bold text-gray-900 text-xs mb-1 leading-tight">{f.title}</div>
            <div className="text-gray-500 text-[10px] leading-relaxed">{f.desc}</div>
          </div>
        ))}
      </div>

      {/* Supplier spotlight */}
      <div className="mb-10">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-[#CC0000] text-xs font-semibold uppercase tracking-wider mb-1">Supplier Spotlight</div>
            <h3 className="text-xl font-extrabold text-gray-900">Featured Canadian Suppliers</h3>
          </div>
          <Link
            to="/products"
            className="text-[#CC0000] text-sm font-semibold hover:underline cursor-pointer whitespace-nowrap flex items-center gap-1"
          >
            View All <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {canadaSuppliers.map((sup) => (
            <div
              key={sup.name}
              className="bg-white border border-gray-100 rounded-xl overflow-hidden hover:border-[#CC0000]/40 transition-all group cursor-pointer"
            >
              <div className="relative h-36 overflow-hidden">
                <img
                  src={sup.image}
                  alt={sup.name}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                <div className="absolute top-2 left-2 bg-[#CC0000] text-white text-[10px] font-bold px-2 py-0.5 rounded">
                  {sup.badge}
                </div>
                <div className="absolute top-2 right-2 text-lg">🇨🇦</div>
              </div>
              <div className="p-4">
                <h4 className="font-bold text-gray-900 text-sm leading-tight mb-1">{sup.name}</h4>
                <div className="flex items-center gap-1 mb-1">
                  <i className="ri-map-pin-line text-[10px] text-gray-400"></i>
                  <span className="text-[10px] text-gray-400">{sup.location}</span>
                </div>
                <div className="text-[10px] text-[#CC0000] font-medium mb-2">{sup.category}</div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1">
                    <i className="ri-star-fill text-[#D4AF37] text-xs"></i>
                    <span className="text-xs font-bold text-gray-700">{sup.rating}</span>
                  </div>
                  <span className="text-[10px] text-gray-400">{sup.products} products</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom CTA banner */}
      <div className="relative rounded-xl overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=panoramic%20view%20of%20canadian%20city%20skyline%20toronto%20with%20maple%20leaf%20flag%20and%20modern%20buildings%2C%20beautiful%20sunset%20sky%20with%20red%20and%20orange%20colors%2C%20professional%20commercial%20photography%2C%20wide%20angle%20cityscape&width=1400&height=200&seq=canada-cta-bg&orientation=landscape"
          alt="Canada CTA"
          className="w-full h-40 object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#CC0000]/90 via-[#CC0000]/75 to-[#8B0000]/80" />
        <div className="absolute inset-0 flex flex-col sm:flex-row items-center justify-between px-8 gap-4">
          <div>
            <div className="text-white font-extrabold text-xl md:text-2xl leading-tight">
              🇨🇦 Canadian? Order Direct — Ships from Canada
            </div>
            <div className="text-white/80 text-sm mt-1">
              No import fees · Bilingual support · 3–6 day delivery across all provinces
            </div>
          </div>
          <div className="flex flex-wrap gap-3 flex-shrink-0">
            <Link
              to="/products"
              className="bg-white text-[#CC0000] font-bold px-6 py-2.5 rounded-full text-sm hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              Shop Now
            </Link>
            <a
              href="https://wa.me/1234567890"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-[#25D366] text-white font-bold px-6 py-2.5 rounded-full text-sm hover:bg-[#1ebe5d] transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-whatsapp-line"></i> WhatsApp Us
            </a>
          </div>
        </div>
      </div>

    </div>
  </section>
);

export default CanadaDirectSection;
